(() => {
  if (customElements.get('mc-mobile-shopping')) return;

  class MobileShopping extends HTMLElement {
    connectedCallback() {
      if (this.initialized) return;
      this.initialized = true;
      this.controller = new AbortController();
      this.eventOptions = { signal: this.controller.signal };
      this.mobile = window.matchMedia('(max-width: 1024px)');
      this.nav = this.querySelector('.mc-mobile-nav');
      this.dialog = this.querySelector('dialog');
      this.categories = this.querySelector('[data-mc-categories]');
      this.categories.addEventListener('click', (event) => {
        if (!this.mobile.matches || typeof this.dialog.showModal !== 'function') return;
        event.preventDefault();
        this.dialog.showModal();
        this.categories.setAttribute('aria-expanded', 'true');
        document.body.classList.add('mc-categories-open');
      }, this.eventOptions);
      this.querySelector('[data-mc-close]').addEventListener('click', () => this.dialog.close(), this.eventOptions);
      this.dialog.addEventListener('click', (event) => {
        if (event.target !== this.dialog) return;
        const rect = this.dialog.getBoundingClientRect();
        if (event.clientY < rect.top || event.clientX < rect.left || event.clientX > rect.right) this.dialog.close();
      }, this.eventOptions);
      this.dialog.addEventListener('close', () => {
        document.body.classList.remove('mc-categories-open');
        this.categories.setAttribute('aria-expanded', 'false');
        this.categories.focus({ preventScroll: true });
      }, this.eventOptions);
      this.mobile.addEventListener('change', () => {
        if (!this.mobile.matches && this.dialog.open) this.dialog.close();
        this.updateVisibility();
      }, this.eventOptions);
      this.initPurchase();
    }

    initPurchase() {
      this.purchase = this.querySelector('.mc-mobile-purchase');
      const productRoot = document.querySelector('#MainContent [id^="ProductSection-"]') || document.getElementById('MainContent');
      this.form = document.getElementById(`product-form-${this.dataset.productId}`)
        || productRoot?.querySelector('.mc-add-to-cart')?.closest('form');
      this.originalButton = this.form?.querySelector('[name="add"]');
      if (!this.purchase || !this.originalButton || !('IntersectionObserver' in window)) return;
      this.options = document.getElementById(`product-option-${this.dataset.productId}`);
      this.price = document.getElementById(`product-price-${this.dataset.productId}`);
      this.add = this.querySelector('[data-mc-add]');
      this.optionSelected = this.dataset.hasOptions !== 'true' || this.dataset.optionSelected === 'true';
      this.pastButton = false;
      this.buying = false;
      document.body.classList.add('mc-mobile-purchase-ready');
      this.observer = new IntersectionObserver(([entry]) => {
        this.pastButton = !entry.isIntersecting && entry.boundingClientRect.bottom <= 0;
        this.updateVisibility();
      });
      this.observer.observe(this.originalButton);
      this.mutations = new MutationObserver(() => this.syncPurchase());
      this.mutations.observe(this.form, { childList: true, subtree: true, attributes: true, attributeFilter: ['disabled', 'aria-disabled', 'class', 'value'], characterData: true });
      if (this.price) this.mutations.observe(this.price, { childList: true, subtree: true, attributes: true, characterData: true });
      this.options?.addEventListener('change', () => {
        this.optionSelected = true;
        this.syncPurchase();
      }, this.eventOptions);
      this.form.addEventListener('change', () => this.syncPurchase(), this.eventOptions);
      this.form.addEventListener('input', () => this.syncPurchase(), this.eventOptions);
      this.form.addEventListener('invalid', () => this.revealOptions(), { ...this.eventOptions, capture: true });
      this.querySelector('[data-mc-product-options]').addEventListener('click', () => this.revealOptions(), this.eventOptions);
      this.add.addEventListener('click', () => {
        if (!this.optionSelected) {
          this.revealOptions();
          return;
        }
        this.syncPurchase();
        if (this.add.disabled) return;
        this.buying = true;
        this.syncPurchase();
        // Reuse the exact main product button: variant, quantity, validation and cart behaviour stay native.
        this.originalButton.click();
        this.releaseTimer = window.setTimeout(() => {
          this.buying = false;
          this.syncPurchase();
        }, 1200);
      }, this.eventOptions);
      this.syncPurchase();
      this.updateVisibility();
    }

    syncPurchase() {
      if (!this.add) return;
      this.originalButton = this.form.querySelector('[name="add"]');
      if (!this.originalButton) {
        this.purchase.hidden = true;
        this.nav.hidden = false;
        return;
      }
      const priceRoot = this.price?.querySelector('.price');
      const priceSelector = priceRoot?.classList.contains('price--on-sale') ? '.price__sale .price-item--sale' : '.price__regular .price-item--regular';
      const priceText = this.price?.querySelector(priceSelector)?.textContent.trim();
      if (priceText) this.querySelector('[data-mc-price]').textContent = priceText;
      const choices = this.options ? [...this.options.querySelectorAll('input[type="radio"]:checked, select')].map(input => input.value).filter(Boolean) : [];
      const quantity = this.form.elements.namedItem('quantity')?.value || '1';
      const optionText = this.optionSelected ? choices.join(' / ') : this.dataset.chooseLabel;
      this.querySelector('[data-mc-variant]').textContent = [optionText, `${this.dataset.quantityLabel} ${quantity}`].filter(Boolean).join(' · ');
      const busy = this.buying || this.originalButton.classList.contains('loading') || this.originalButton.getAttribute('aria-busy') === 'true';
      const unavailable = this.originalButton.disabled || this.originalButton.getAttribute('aria-disabled') === 'true';
      this.add.disabled = busy || (this.optionSelected && unavailable);
      this.add.textContent = busy ? this.dataset.addingLabel : !this.optionSelected ? this.dataset.chooseLabel : this.originalButton.textContent.trim();
      this.add.setAttribute('aria-busy', String(busy));
      const error = this.form.querySelector('.product-form__error-message');
      if (this.buying && error?.textContent.trim() && error.getClientRects().length) this.revealOptions();
    }

    updateVisibility() {
      if (!this.purchase || !this.add) return;
      const show = this.mobile.matches && this.pastButton;
      this.purchase.hidden = !show;
      this.nav.hidden = show;
    }

    revealOptions() {
      const target = this.options || this.originalButton;
      if (!target) return;
      target.scrollIntoView({ behavior: window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth', block: 'center' });
      const focusTarget = target.querySelector?.('select, input:checked, input:not([type="hidden"])') || this.originalButton;
      focusTarget?.focus({ preventScroll: true });
    }

    disconnectedCallback() {
      this.controller?.abort();
      this.observer?.disconnect();
      this.mutations?.disconnect();
      window.clearTimeout(this.releaseTimer);
      if (this.dialog?.open) this.dialog.close();
      document.body.classList.remove('mc-categories-open', 'mc-mobile-purchase-ready');
      this.initialized = false;
    }
  }

  customElements.define('mc-mobile-shopping', MobileShopping);
})();
